import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Lightweight table primitives matching the `.nv-table` CSS treatment in the
 * design HTML (`quotes.html`, `client_overview.html`). Renders a plain
 * `<table>`/`<thead>`/`<tbody>`/`<tr>`/`<th>`/`<td>` element tree; consumers
 * apply layout/width classes via `className`.
 *
 * For sorting, virtualization, or column resizing reach for
 * `@tanstack/react-table` and compose it with these primitives.
 */
export type TableProps = React.TableHTMLAttributes<HTMLTableElement>;

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn('nv-table', className)} {...props} />
  ),
);
Table.displayName = 'Table';

/**
 * `<thead>` section wrapper. Use this as the direct child of `<Table>` to
 * contain the header row(s).
 *
 * Naming follows shadcn/ui: `TableHeader` = `<thead>`, `TableHead` = `<th>`.
 */
export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={className} {...props} />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
));
TableBody.displayName = 'TableBody';

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr ref={ref} className={className} {...props} />
));
TableRow.displayName = 'TableRow';

/**
 * `<th>` column-header cell. Use inside `<TableHeader><TableRow>` for column
 * labels. Naming follows shadcn/ui convention.
 */
export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th ref={ref} className={className} scope="col" {...props} />
));
TableHead.displayName = 'TableHead';

// TableHeaderCell is kept as an alias for TableHead for backward compatibility.
// New code should use TableHead directly.
export const TableHeaderCell = TableHead;

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={className} {...props} />
));
TableCell.displayName = 'TableCell';

export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t font-medium', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

export const TableCaption = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableCaptionElement>) => (
  <caption
    className={cn('mt-4 text-[12.5px] text-ink-muted', className)}
    {...props}
  />
);
TableCaption.displayName = 'TableCaption';
